import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import AppLayout from "@/layouts/AppLayout"
import { Head, useForm } from "@inertiajs/react"
import { IconArrowLeft } from "@tabler/icons-react"

const LEVEL_GROUPS = [
  { name: "Junior", emoji: "\u{1F423}" },
  { name: "Advanced", emoji: "\u{1F425}" },
  { name: "SemiSenior", emoji: "\u{1F986}" },
  { name: "Senior", emoji: "\u{1F985}" },
  { name: "Staff", emoji: "\u{1F426}\u200D\u2B1B" },
  { name: "Principal", emoji: "\u{1F989}" },
]

export default function SeniorityLevelNew() {
  const { data, setData, post, processing, errors } = useForm({
    title: "",
    level: "",
    level_group: "",
    sub_level: "",
    keyword: "",
    hourly_rate: "",
    pl_incremental: "",
    monthly_6h: "",
    irpf_6h_monthly: "",
    monthly_8h: "",
    irpf_8h_monthly: "",
    description: "",
    active: "true",
  })

  const allErrors = Object.values(errors)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post("/admin/seniority_levels")
  }

  return (
    <>
      <Head title="New Seniority Level - Operations Center" />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/admin/seniority_levels">Seniority Levels</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>New</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href="/admin/seniority_levels">
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <h1 className="text-lg font-semibold tracking-tight">New Seniority Level</h1>
          </div>
        </div>

        <div className="max-w-2xl">
          <form onSubmit={handleSubmit}>
            <div className="rounded-xl border bg-card">
              <div className="p-6">
                {allErrors.length > 0 && (
                  <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                    <ul className="space-y-1 text-sm text-destructive">
                      {allErrors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="space-y-5">
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="title">Level Title</Label>
                      <Input
                        id="title"
                        value={data.title}
                        onChange={(e) => setData("title", e.target.value)}
                        placeholder="e.g., Junior 1"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="level">Level Number</Label>
                      <Input
                        id="level"
                        type="number"
                        step="1"
                        value={data.level}
                        onChange={(e) => setData("level", e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">Unique number for ordering levels</p>
                    </div>

                    <div className="space-y-2">
                      <Label>Level Group</Label>
                      <Select value={data.level_group} onValueChange={(v) => setData("level_group", v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a group" />
                        </SelectTrigger>
                        <SelectContent>
                          {LEVEL_GROUPS.map((g) => (
                            <SelectItem key={g.name} value={g.name}>
                              {g.emoji} {g.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="sub_level">Sub Level</Label>
                      <Input
                        id="sub_level"
                        type="number"
                        step="1"
                        min="1"
                        value={data.sub_level}
                        onChange={(e) => setData("sub_level", e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">Position within the level group</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="keyword">Keyword</Label>
                      <Input
                        id="keyword"
                        value={data.keyword}
                        onChange={(e) => setData("keyword", e.target.value)}
                        placeholder="e.g., JR1, SR3"
                      />
                      <p className="text-xs text-muted-foreground">Short identifier</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="hourly_rate">Hourly Rate ($)</Label>
                      <Input
                        id="hourly_rate"
                        type="number"
                        step="0.01"
                        min="0"
                        value={data.hourly_rate}
                        onChange={(e) => setData("hourly_rate", e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="pl_incremental">P/L Incremental</Label>
                      <Input
                        id="pl_incremental"
                        type="number"
                        step="0.01"
                        min="0"
                        value={data.pl_incremental}
                        onChange={(e) => setData("pl_incremental", e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">Growth factor relative to previous level</p>
                    </div>
                  </div>

                  <Separator />
                  <h3 className="text-sm font-semibold">Compensation Details</h3>

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="space-y-2">
                      <Label htmlFor="monthly_6h">6H Monthly ($)</Label>
                      <Input
                        id="monthly_6h"
                        type="number"
                        step="0.01"
                        min="0"
                        value={data.monthly_6h}
                        onChange={(e) => setData("monthly_6h", e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="irpf_6h_monthly">IRPF 6H Monthly ($)</Label>
                      <Input
                        id="irpf_6h_monthly"
                        type="number"
                        step="0.01"
                        min="0"
                        value={data.irpf_6h_monthly}
                        onChange={(e) => setData("irpf_6h_monthly", e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="monthly_8h">8H Monthly ($)</Label>
                      <Input
                        id="monthly_8h"
                        type="number"
                        step="0.01"
                        min="0"
                        value={data.monthly_8h}
                        onChange={(e) => setData("monthly_8h", e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="irpf_8h_monthly">IRPF 8H Monthly ($)</Label>
                      <Input
                        id="irpf_8h_monthly"
                        type="number"
                        step="0.01"
                        min="0"
                        value={data.irpf_8h_monthly}
                        onChange={(e) => setData("irpf_8h_monthly", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">
                      Description <span className="font-normal text-muted-foreground">(optional)</span>
                    </Label>
                    <textarea
                      id="description"
                      rows={3}
                      className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                      value={data.description}
                      onChange={(e) => setData("description", e.target.value)}
                      placeholder="Responsibilities and expectations"
                    />
                  </div>
                </div>
              </div>

              <Separator />
              <div className="flex justify-end gap-2 p-4">
                <Button variant="ghost" asChild>
                  <a href="/admin/seniority_levels">Cancel</a>
                </Button>
                <Button type="submit" disabled={processing}>
                  {processing ? "Creating..." : "Create Level"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

SeniorityLevelNew.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
